import { useEffect, useState, useCallback } from "react";
import izenuAPI from "../../services/izenuAPI";

const EMPTY = {
    title: "",
    artist: "",
    stream_url: "",
    artwork_url: "",
    tagline: "",
    accent_color: "#6366f1",
    is_active: 1,
};

export default function Radios() {
    const [radios, setRadios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [imageFile, setImageFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(null);
    const [audio, setAudio] = useState(null);

    const fetchRadios = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await izenuAPI.get("/admin/radios/list.php");
            setRadios(data?.data || []);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchRadios(); }, [fetchRadios]);

    const startEdit = (r) => {
        setEditing(r.id);
        setForm({
            title: r.title || "",
            artist: r.artist || "",
            stream_url: r.stream_url || "",
            artwork_url: r.artwork_url || "",
            tagline: r.tagline || "",
            accent_color: r.accent_color || "#6366f1",
            is_active: r.is_active ?? 1,
        });
        setImageFile(null);
    };

    const startNew = () => { setEditing("new"); setForm(EMPTY); setImageFile(null); };
    const cancelEdit = () => { setEditing(null); setForm(EMPTY); setImageFile(null); };

    const uploadImageIfAny = async () => {
        if (!imageFile) return null;
        const fd = new FormData();
        fd.append("image", imageFile);
        const { data } = await izenuAPI.post("/admin/radios/uploadImage.php", fd);
        return data?.image || null;
    };

    const save = async () => {
        if (!form.title || !form.stream_url) {
            alert("Título e Stream URL são obrigatórios");
            return;
        }
        setSaving(true);
        try {
            const imageName = await uploadImageIfAny();
            const payload = { ...form };
            if (imageName) payload.image = imageName;
            if (editing === "new") {
                await izenuAPI.post("/admin/radios/create.php", payload);
            } else {
                await izenuAPI.post(`/admin/radios/update.php?id=${editing}`, payload);
            }
            await fetchRadios();
            cancelEdit();
        } catch (e) {
            alert("Erro: " + (e?.response?.data?.message || e.message));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm("Eliminar esta rádio? A ação é irreversível.")) return;
        try {
            await izenuAPI.delete(`/admin/radios/delete.php?id=${id}`);
            await fetchRadios();
        } catch (e) {
            alert("Erro: " + (e?.response?.data?.message || e.message));
        }
    };

    const move = async (id, dir) => {
        const idx = radios.findIndex((r) => r.id === id);
        if (idx < 0) return;
        const newIdx = idx + dir;
        if (newIdx < 0 || newIdx >= radios.length) return;
        const newOrder = [...radios];
        [newOrder[idx], newOrder[newIdx]] = [newOrder[newIdx], newOrder[idx]];
        setRadios(newOrder);
        try {
            await izenuAPI.post("/admin/radios/reorder.php", { ids: newOrder.map((r) => r.id) });
        } catch (e) {
            alert("Erro: " + (e?.response?.data?.message || e.message));
            fetchRadios();
        }
    };

    const toggleTest = (r) => {
        if (testing === r.id) { audio?.pause(); setAudio(null); setTesting(null); return; }
        audio?.pause();
        const a = new Audio(r.stream_url);
        a.play().catch((err) => alert("Erro ao reproduzir: " + err.message));
        setAudio(a);
        setTesting(r.id);
    };

    return (
        <div style={s.container}>
            <div style={s.header}>
                <div>
                    <h1 style={s.title}>Rádios</h1>
                    <p style={s.subtitle}>Gere as rádios em directo disponíveis na app e web.</p>
                </div>
                {editing === null && (
                    <button onClick={startNew} style={s.primaryBtn}>+ Adicionar rádio</button>
                )}
            </div>

            {editing !== null && (
                <div style={s.card}>
                    <h3 style={s.cardTitle}>{editing === "new" ? "Nova rádio" : `Editar rádio #${editing}`}</h3>
                    <div style={s.formGrid}>
                        <Field label="Título *">
                            <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} style={s.input} />
                        </Field>
                        <Field label="Artista / emissora">
                            <input type="text" value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} style={s.input} />
                        </Field>
                        <Field label="Stream URL *" wide>
                            <input type="text" value={form.stream_url} onChange={(e) => setForm({ ...form, stream_url: e.target.value })} style={s.input} />
                        </Field>
                        <Field label="Artwork URL (remoto, opcional)" wide>
                            <input type="text" value={form.artwork_url} onChange={(e) => setForm({ ...form, artwork_url: e.target.value })} style={s.input} />
                        </Field>
                        <Field label="Tagline" wide>
                            <input type="text" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} style={s.input} />
                        </Field>
                        <Field label="Cor de destaque">
                            <div style={{ display: "flex", gap: 8 }}>
                                <input type="color" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                                    style={{ ...s.input, width: 60, padding: 4, cursor: "pointer" }} />
                                <input type="text" value={form.accent_color} onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                                    style={{ ...s.input, flex: 1 }} />
                            </div>
                        </Field>
                        <Field label="Estado">
                            <select value={form.is_active} onChange={(e) => setForm({ ...form, is_active: parseInt(e.target.value) })} style={s.input}>
                                <option value={1}>Activa</option>
                                <option value={0}>Inactiva</option>
                            </select>
                        </Field>
                        <Field label="Imagem (upload)" wide>
                            <input type="file" accept="image/jpeg,image/png,image/webp"
                                onChange={(e) => setImageFile(e.target.files?.[0] || null)} style={{ ...s.input, padding: 8 }} />
                            {imageFile && <p style={{ fontSize: 12, color: "#6366f1", margin: "6px 0 0" }}>Selecionado: {imageFile.name}</p>}
                        </Field>
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                        <button onClick={save} disabled={saving} style={{ ...s.primaryBtn, opacity: saving ? 0.5 : 1 }}>
                            {saving ? "A gravar..." : "Gravar"}
                        </button>
                        <button onClick={cancelEdit} style={s.ghostBtn}>Cancelar</button>
                    </div>
                </div>
            )}

            {loading ? (
                <p style={s.empty}>A carregar...</p>
            ) : radios.length === 0 ? (
                <p style={s.empty}>Nenhuma rádio cadastrada.</p>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    {radios.map((r, i) => (
                        <div key={r.id} style={{ ...s.listCard, borderLeft: `4px solid ${r.accent_color || "#6366f1"}` }}>
                            <div style={s.thumbBox}>
                                {r.image
                                    ? <img src={r.image} alt="" style={s.thumbImg} />
                                    : <span style={{ fontSize: 11, color: "#bbb" }}>sem imagem</span>}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <h3 style={s.itemTitle}>{r.title}</h3>
                                    <span style={s.idBadge}>#{r.id}</span>
                                    {!r.is_active && <span style={s.inactiveBadge}>INACTIVA</span>}
                                </div>
                                <p style={s.itemSub}>{r.artist}{r.artist && r.tagline ? " · " : ""}{r.tagline}</p>
                                <p style={s.itemUrl}>{r.stream_url}</p>
                            </div>
                            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                <button onClick={() => move(r.id, -1)} disabled={i === 0} style={{ ...s.iconBtn, opacity: i === 0 ? 0.3 : 1 }}>↑</button>
                                <button onClick={() => move(r.id, 1)} disabled={i === radios.length - 1} style={{ ...s.iconBtn, opacity: i === radios.length - 1 ? 0.3 : 1 }}>↓</button>
                                <button onClick={() => toggleTest(r)}
                                    style={{ ...s.smallBtn, background: testing === r.id ? "#ef4444" : "#e5e7eb", color: testing === r.id ? "#fff" : "#374151" }}>
                                    {testing === r.id ? "⏸ Parar" : "▶ Testar"}
                                </button>
                                <button onClick={() => startEdit(r)} style={{ ...s.smallBtn, background: "#3b82f6", color: "#fff" }}>Editar</button>
                                <button onClick={() => remove(r.id)} style={{ ...s.smallBtn, background: "#fff", color: "#ef4444", border: "1px solid #ef4444" }}>Eliminar</button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Field({ label, wide, children }) {
    return (
        <div style={{ gridColumn: wide ? "1 / -1" : "auto" }}>
            <label style={{ display: "block", fontSize: 12, color: "#666", fontWeight: 500, marginBottom: 4 }}>{label}</label>
            {children}
        </div>
    );
}

const s = {
    container: { padding: "0 20px 40px 20px", maxWidth: 1300 },
    header: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 700, color: "#1a1a2e", margin: 0 },
    subtitle: { margin: "4px 0 0 0", fontSize: 13, color: "#999", fontWeight: 400 },
    primaryBtn: { padding: "10px 18px", background: "#6366f1", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 14 },
    ghostBtn: { padding: "10px 18px", background: "#f3f4f6", color: "#374151", border: "none", borderRadius: 8, fontWeight: 500, cursor: "pointer", fontSize: 14 },
    card: { padding: 24, backgroundColor: "#fff", borderRadius: 12, boxShadow: "0 1px 8px rgba(0,0,0,0.06)", border: "1px solid #f0f0f0", marginBottom: 20 },
    cardTitle: { fontSize: 15, fontWeight: 600, color: "#333", margin: "0 0 14px 0" },
    formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 },
    input: { width: "100%", padding: "8px 12px", fontSize: 14, border: "1px solid #d1d5db", borderRadius: 6, background: "#fff", color: "#1f2937", outline: "none" },
    empty: { color: "#ccc", textAlign: "center", padding: 40, fontSize: 14 },
    listCard: { display: "flex", alignItems: "center", gap: 14, padding: 14, background: "#fff", borderRadius: 10, boxShadow: "0 1px 6px rgba(0,0,0,0.05)", border: "1px solid #f0f0f0" },
    thumbBox: { width: 56, height: 56, borderRadius: 8, overflow: "hidden", background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
    thumbImg: { width: "100%", height: "100%", objectFit: "cover" },
    itemTitle: { margin: 0, fontSize: 15, fontWeight: 600, color: "#1f2937" },
    itemSub: { margin: "2px 0 0", fontSize: 13, color: "#6b7280" },
    itemUrl: { margin: "2px 0 0", fontSize: 11, color: "#aaa", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 600 },
    idBadge: { fontSize: 11, color: "#9ca3af", background: "#f3f4f6", padding: "2px 6px", borderRadius: 4 },
    inactiveBadge: { fontSize: 10, color: "#fff", background: "#ef4444", padding: "2px 6px", borderRadius: 4, fontWeight: 700 },
    iconBtn: { width: 32, height: 32, border: "none", borderRadius: 6, background: "#f3f4f6", color: "#374151", cursor: "pointer", fontSize: 16 },
    smallBtn: { padding: "6px 12px", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 12, fontWeight: 600 },
};
